import React, { useState } from "react";
import {
  Box,
  Typography,
  ButtonGroup,
  Button,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";

// プラン定義
const priceTiers = [
  { date: "2024-04-30", price: 29800, label: "プランA" },
  { date: "2024-05-30", price: 39800, label: "プランB" },
  { date: "2024-06-30", price: 49800, label: "プランC" },
];

// テーマ選択肢
const styleOptions = {
  A: "やさしい・自然な雰囲気",
  B: "ポップでにぎやか",
  C: "ファンタジー・夢の世界",
  D: "北欧風ミニマル",
};

// 日付比較
const getStatus = (date) => {
  const now = dayjs();
  return dayjs(date).isBefore(now, "day") ? "expired" : "active";
};

// テーマごとのスタイル設定
const getThemeStyle = (themeType, index, status, isHighlight) => {
  const styles = {
    A: {
      bg: ["#FFF8E1", "#FAF3E0", "#F1F8E9"],
      btn: "#A5D6A7",
      font: "'M PLUS Rounded 1c', sans-serif",
    },
    B: {
      bg: ["#FFCDD2", "#FFF176", "#81D4FA"],
      btn: "#FF4081",
      font: "'Comic Sans MS', cursive",
    },
    C: {
      bg: ["#EDE7F6", "#FCE4EC", "#E1F5FE"],
      btn: "#9575CD",
      font: "'Noto Serif JP', serif",
    },
    D: {
      bg: ["#F0F0F0", "#E8F5E9", "#E3F2FD"],
      btn: "#90A4AE",
      font: "'Noto Sans JP', sans-serif",
    },
  };

  const theme = styles[themeType] || styles.A;

  return {
    backgroundColor: status === "expired" ? "#eeeeee" : theme.bg[index],
    fontFamily: theme.font,
    buttonColor: theme.btn,
  };
};

// メインコンポーネント
const TieredPricing = () => {
  const [themeType, setThemeType] = useState("A");
  const today = dayjs();

  const currentIndex = priceTiers.findIndex((tier) =>
    today.isBefore(dayjs(tier.date), "day")
  );
  const highlightIndex =
    currentIndex === -1 ? priceTiers.length - 1 : currentIndex;

  return (
    <Box>
      <Typography variant="h5" align="center" gutterBottom>
        テーマを選んでみよう 🎨
      </Typography>

      <Box display="flex" justifyContent="center" mb={3}>
        <ButtonGroup variant="outlined">
          {Object.entries(styleOptions).map(([key, label]) => (
            <Button
              key={key}
              variant={themeType === key ? "contained" : "outlined"}
              onClick={() => setThemeType(key)}
            >
              {key}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      <Box
        display="flex"
        borderRadius={4}
        overflow="hidden"
        boxShadow="0 2px 10px rgba(0,0,0,0.08)"
        width="100%"
        maxWidth={900}
        mx="auto"
        mt={4}
      >
        {priceTiers.map((tier, index) => {
          const status = getStatus(tier.date);
          const isHighlight = index === highlightIndex;
          const { backgroundColor, fontFamily, buttonColor } = getThemeStyle(
            themeType,
            index,
            status,
            isHighlight
          );

          return (
            <Box
              key={index}
              flex={1}
              p={4}
              textAlign="center"
              bgcolor={backgroundColor}
              borderRight={
                index !== priceTiers.length - 1 ? "1px solid #ccc" : "none"
              }
              sx={{
                opacity: status === "expired" ? 0.5 : 1,
                fontFamily,
                transition: "all 0.3s ease",
                borderTopLeftRadius: index === 0 ? 24 : 0,
                borderBottomLeftRadius: index === 0 ? 24 : 0,
                borderTopRightRadius: index === priceTiers.length - 1 ? 24 : 0,
                borderBottomRightRadius: index === priceTiers.length - 1 ? 24 : 0,
              }}
            >
              <Typography variant="h6">{tier.label}</Typography>
              <Typography variant="h4" mt={1}>
                ￥{tier.price.toLocaleString()}
              </Typography>
              <Box my={1}>
                {status === "expired" ? (
                  isHighlight ? (
                    <Chip label="最終価格" color="warning" />
                  ) : (
                    <Chip label="締切" color="default" />
                  )
                ) : isHighlight ? (
                  <Chip label="おすすめ" color="success" />
                ) : null}
              </Box>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: buttonColor,
                  color: "#fff",
                  mt: 1,
                  borderRadius: "16px",
                  "&:hover": {
                    backgroundColor: buttonColor,
                    opacity: 0.9,
                  },
                }}
                onClick={() =>
                  alert(`￥${tier.price.toLocaleString()} が選ばれました`)
                }
              >
                このプランを選ぶ
              </Button>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TieredPricing;
